/**
 * Authenticated agent WebSocket client (R7.2, R8.1).
 *
 * Connects to the interview agent on AgentCore Runtime:
 *   wss://bedrock-agentcore.<region>.amazonaws.com/runtimes/<arn>/ws
 * A browser WebSocket cannot set an Authorization header, so the Cognito
 * access token travels in the Sec-WebSocket-Protocol header as
 * "base64UrlBearerAuthorization.<base64url token>", alongside the sentinel
 * protocol "base64UrlBearerAuthorization". AgentCore's JWT authorizer checks
 * it at the handshake. The client then sends { action: "auth", token } so the
 * agent can bind the connection to the token's identity, and waits for
 * { type: "ready" } before any turn is sent.
 *
 * The runtime session id (>= 33 chars) is the interview session id, so every
 * connection for one interview lands in the same isolated runtime session.
 *
 * The underlying socket is behind a port so the client is testable without a
 * real network.
 */
import { AuthGate } from './auth-gate';
import { base64UrlEncodeString } from './cognito-auth';

export interface FormStateSlot {
  slotId: string;
  key: string;
  label: string;
  dataType: string;
  required: boolean;
  state: string;
  value: string | number | boolean | null;
  current: boolean;
  confidence?: number;
}
export interface FormStateSection {
  sectionId: string;
  name: string;
  committed: boolean;
  slots: FormStateSlot[];
}
export interface FormState {
  formId: string;
  formVersion: string;
  name: string;
  status: string;
  currentSlotKey: string | null;
  totals: { required: number; filled: number };
  sections: FormStateSection[];
}

/** Interview messages the view model renders. */
export type ServerMessage =
  | { type: 'prompt'; prompt: string; formState?: FormState }
  | { type: 'complete'; summary: string; formState?: FormState }
  | { type: 'reprompt'; reason: string; message: string; formState?: FormState }
  | { type: 'transcript'; text: string; formState?: FormState };

/** Spoken-prompt audio events. */
export type AudioMessage = { type: 'audio'; data: string; sampleRate: number } | { type: 'audio_end' };

/** Voice-capture progress from the agent. */
export type VoiceEvent =
  | { type: 'audio_ack'; ms: number }
  | { type: 'transcript_partial'; text: string }
  | { type: 'listening_stopped'; reason: string };

const VOICE_EVENT_TYPES = new Set(['audio_ack', 'transcript_partial', 'listening_stopped']);

export interface Socket {
  connect(url: string, protocols?: string[]): Promise<void>;
  send(data: string): Promise<void>;
  onMessage(handler: (data: string) => void): void;
  close(): Promise<void>;
}

export interface TurnPayload {
  formId: string;
  formVersion: string;
  sessionId: string;
  locale: string;
  utterance: string | null;
  turnId: string;
  /** Ask the agent to speak the reply with Nova Sonic. */
  speak?: boolean;
}

export type AudioStartPayload = Omit<TurnPayload, 'utterance'> & {
  /** Stream to Nova Sonic as captured; the agent detects the end of the answer. */
  live?: boolean;
};

export interface AgentEndpoint {
  region: string;
  runtimeArn: string;
}

export const BEARER_PROTOCOL = 'base64UrlBearerAuthorization';
const RUNTIME_SESSION_PARAM = 'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id';
const MIN_RUNTIME_SESSION_ID = 33;
const READY_TIMEOUT_MS = 15_000;

const INTERVIEW_TYPES = new Set(['prompt', 'complete', 'reprompt', 'transcript']);

/** The AgentCore Runtime WebSocket URL for one interview session. */
export function agentWebSocketUrl(endpoint: AgentEndpoint, sessionId: string): string {
  if (sessionId.length < MIN_RUNTIME_SESSION_ID) {
    throw new Error(`session id must be at least ${MIN_RUNTIME_SESSION_ID} characters`);
  }
  const base = `wss://bedrock-agentcore.${endpoint.region}.amazonaws.com`;
  return (
    `${base}/runtimes/${encodeURIComponent(endpoint.runtimeArn)}/ws` +
    `?${RUNTIME_SESSION_PARAM}=${encodeURIComponent(sessionId)}`
  );
}

/** WebSocket subprotocols carrying a bearer token for a browser client. */
export function bearerProtocols(token: string): string[] {
  return [`${BEARER_PROTOCOL}.${base64UrlEncodeString(token)}`, BEARER_PROTOCOL];
}

export class InterviewSocketClient {
  private handlers: Array<(m: ServerMessage) => void> = [];
  private audioHandlers: Array<(m: AudioMessage) => void> = [];
  private voiceHandlers: Array<(m: VoiceEvent) => void> = [];
  private errorHandlers: Array<(reason: string, raw: string) => void> = [];
  private ready = false;
  /** The access token the agent last accepted on this connection. */
  private sentToken = '';
  private readyWaiter: { resolve: () => void; reject: (e: Error) => void } | null = null;

  constructor(
    private readonly socket: Socket,
    private readonly authGate: AuthGate,
    private readonly endpoint: AgentEndpoint,
  ) {}

  /** Connect for one interview session and wait until the agent is ready. */
  async open(sessionId: string): Promise<void> {
    const session = await this.authGate.requireSession();
    this.ready = false;
    this.socket.onMessage((data) => this.dispatch(data));
    await this.socket.connect(agentWebSocketUrl(this.endpoint, sessionId), bearerProtocols(session.accessToken));
    await this.authenticate(session.accessToken);
  }

  /** Send { action: "auth" } and wait for the agent's ready (or a rejection). */
  private async authenticate(token: string): Promise<void> {
    const ready = new Promise<void>((resolve, reject) => {
      this.readyWaiter = { resolve, reject };
    });
    const timer = setTimeout(() => this.readyWaiter?.reject(new Error('agent did not acknowledge sign-in')), READY_TIMEOUT_MS);
    try {
      await this.socket.send(JSON.stringify({ action: 'auth', token }));
      await ready;
      this.sentToken = token;
    } finally {
      clearTimeout(timer);
      this.readyWaiter = null;
    }
  }

  /**
   * Before a turn: get a valid session (the provider refreshes an expiring
   * access token) and, if the token changed, present it on the open socket so
   * a long interview outlives one short-lived access token.
   */
  private async ensureFresh(): Promise<void> {
    if (!this.ready) throw new Error('not authenticated');
    const session = await this.authGate.requireSession();
    if (session.accessToken !== this.sentToken) await this.authenticate(session.accessToken);
  }

  private dispatch(data: string): void {
    let msg: { type?: string; message?: string; code?: string };
    try {
      msg = JSON.parse(data) as typeof msg;
    } catch {
      this.emitError('Received malformed message from server', data);
      return;
    }
    if (!msg || typeof msg !== 'object') {
      this.emitError('Unexpected response from server', data);
      return;
    }
    if (msg.type === 'ready') {
      this.ready = true;
      this.readyWaiter?.resolve();
      return;
    }
    if (msg.type === 'audio' || msg.type === 'audio_end') {
      for (const h of this.audioHandlers) h(msg as AudioMessage);
      return;
    }
    if (VOICE_EVENT_TYPES.has(msg.type ?? '')) {
      for (const h of this.voiceHandlers) h(msg as VoiceEvent);
      return;
    }
    if (msg.type === 'error' || !INTERVIEW_TYPES.has(msg.type ?? '')) {
      const reason = msg.message ?? 'Unexpected response from server';
      if (msg.code === 'unauthenticated') this.readyWaiter?.reject(new Error(reason));
      this.emitError(reason, data);
      return;
    }
    for (const h of this.handlers) h(msg as ServerMessage);
  }

  onServerMessage(handler: (m: ServerMessage) => void): void {
    this.handlers.push(handler);
  }

  onAudio(handler: (m: AudioMessage) => void): void {
    this.audioHandlers.push(handler);
  }

  /** Voice-capture progress: received audio, live words, auto-stop. */
  onVoiceEvent(handler: (m: VoiceEvent) => void): void {
    this.voiceHandlers.push(handler);
  }

  /** Notified when the server sends an error or an unrecognized payload. */
  onError(handler: (reason: string, raw: string) => void): void {
    this.errorHandlers.push(handler);
  }

  private emitError(reason: string, raw: string): void {
    for (const h of this.errorHandlers) h(reason, raw);
  }

  private requireReady(): void {
    if (!this.ready) throw new Error('not authenticated');
  }

  async sendTurn(payload: TurnPayload): Promise<void> {
    await this.ensureFresh();
    await this.socket.send(JSON.stringify({ action: 'turn', ...payload }));
  }

  async startAudio(payload: AudioStartPayload): Promise<void> {
    await this.ensureFresh();
    await this.socket.send(JSON.stringify({ action: 'audio_start', ...payload }));
  }

  /** Send one chunk of 16 kHz 16-bit mono PCM (keep chunks small: <= 32 KB). */
  async sendAudioChunk(pcm: Uint8Array): Promise<void> {
    this.requireReady();
    let bin = '';
    for (const b of pcm) bin += String.fromCharCode(b);
    await this.socket.send(JSON.stringify({ action: 'audio_chunk', data: btoa(bin) }));
  }

  async endAudio(): Promise<void> {
    this.requireReady();
    await this.socket.send(JSON.stringify({ action: 'audio_end' }));
  }

  async close(): Promise<void> {
    this.ready = false;
    await this.socket.close();
  }
}
